import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Printer, Plus, Trash2, RefreshCw } from 'lucide-react';

const OwnerReport = () => {
  const { encoded } = useParams();
  const [owner, setOwner] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [costs, setCosts] = useState([]);
  const [newCost, setNewCost] = useState({ name: '', amount: '', isRecurring: false });
  const [loading, setLoading] = useState(true);
  const [decodeError, setDecodeError] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  // Decode parameters with error handling - using useMemo to parse once
  const params = React.useMemo(() => {
    try {
      // Byte-accurate base64 decode (handles non-Latin1 owner names);
      // identical to plain atob for ASCII payloads, so old links still work
      const json = new TextDecoder().decode(
        Uint8Array.from(atob(encoded), c => c.charCodeAt(0))
      );
      return JSON.parse(json);
    } catch (error) {
      console.error('Failed to decode report parameters:', error);
      return null;
    }
  }, [encoded]);

  // Set decode error state
  React.useEffect(() => {
    if (!params) {
      setDecodeError('Invalid report link. Please request a new report link.');
    }
  }, [params]);

  const { ownerId = '', ownerName = '', weekStart = '', weekOffset = 0 } = params || {};

  // Calculate week range
  const getWeekRange = useCallback(() => {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [weekStart]);

  // Auth header shared by all requests (the rest of the app sends it too)
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`
  }), []);

  // Fetch costs function
  const fetchCosts = useCallback(async () => {
    try {
      const costsRes = await fetch(
        `${process.env.REACT_APP_API_URL}/api/ownerCosts?` +
        `ownerId=${ownerId}&weekStart=${weekStart}`,
        { headers: authHeaders() }
      );
      if (!costsRes.ok) throw new Error(`Failed to fetch costs (${costsRes.status})`);
      const costsData = await costsRes.json();
      setCosts(costsData[0]?.costs || []);
    } catch (error) {
      console.error('Error fetching costs:', error);
    }
  }, [ownerId, weekStart, authHeaders]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch owner details
        const ownerRes = await fetch(`${process.env.REACT_APP_API_URL}/api/owners`, {
          headers: authHeaders()
        });
        if (!ownerRes.ok) throw new Error(`Failed to fetch owners (${ownerRes.status})`);
        const owners = await ownerRes.json();
        const ownerData = owners.find(o => o._id === ownerId);
        setOwner(ownerData);

        // Fetch week's reservations using payouts endpoint
        const { start, end } = getWeekRange();
        const resRes = await fetch(
          `${process.env.REACT_APP_API_URL}/api/payouts/week?` +
          `startDate=${start.toISOString()}&endDate=${end.toISOString()}`,
          { headers: authHeaders() }
        );
        if (!resRes.ok) throw new Error(`Failed to fetch reservations (${resRes.status})`);
        const resData = await resRes.json();
        setReservations(resData);

        // Fetch owner costs for this week
        await fetchCosts();
      } catch (error) {
        // Surface the failure instead of rendering a $0.00 report that
        // looks legitimate
        console.error('Error fetching data:', error);
        setFetchError('Failed to load report data. Please refresh the page.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [ownerId, weekStart, getWeekRange, fetchCosts, authHeaders]);

  // Listen for messages from parent window
  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data?.type === 'refresh-costs') {
        fetchCosts();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [fetchCosts]);

  // Filter reservations for owner's properties
  const getPropertyReservations = (propertyName) => {
    const { start, end } = getWeekRange();
    return reservations.filter(res => {
      const resPropertyName = res.properties && res.properties.length > 0
        ? res.properties[0]?.name
        : null;

      return res.status === 'accepted' &&
        resPropertyName === propertyName &&
        new Date(res.check_out) >= start &&
        new Date(res.check_out) <= end &&
        res.financials?.host?.revenue?.amount > 0;
    }).sort((a, b) => new Date(a.check_in) - new Date(b.check_in));
  };

  // Persist the costs array; alerts and re-syncs from the server on failure
  // so the optimistic UI doesn't keep an unsaved value
  const saveCosts = async (updatedCosts) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/ownerCosts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ ownerId, weekStart, costs: updatedCosts })
      });
      if (!response.ok) throw new Error(`Failed to save costs (${response.status})`);
    } catch (error) {
      console.error('Error saving costs:', error);
      alert('Failed to save costs. Please try again.');
      fetchCosts();
    }
  };

  // Add cost
  const addCost = async () => {
    if (!newCost.name || !newCost.amount) return;

    const updatedCosts = [...costs, {
      ...newCost,
      id: Date.now().toString(),
      amount: parseFloat(newCost.amount)
    }];
    setCosts(updatedCosts);

    await saveCosts(updatedCosts);

    setNewCost({ name: '', amount: '', isRecurring: false });

    if (window.opener) {
      window.opener.postMessage({ type: 'costs-updated', ownerId }, window.location.origin);
    }
  };

  // Toggle recurring status
  const toggleRecurring = async (costId) => {
    const updatedCosts = costs.map(c =>
      c.id === costId ? { ...c, isRecurring: !c.isRecurring } : c
    );
    setCosts(updatedCosts);

    await saveCosts(updatedCosts);

    if (window.opener) {
      window.opener.postMessage({ type: 'costs-updated', ownerId }, window.location.origin);
    }
  };

  // Remove cost
  const removeCost = async (costId) => {
    const updatedCosts = costs.filter(c => c.id !== costId);
    setCosts(updatedCosts);

    await saveCosts(updatedCosts);

    if (window.opener) {
      window.opener.postMessage({ type: 'costs-updated', ownerId }, window.location.origin);
    }
  };

  // Format date helpers
  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric'
    }).format(new Date(date));
  };

  const formatDateRange = (checkIn, checkOut) => {
    return `${formatDate(checkIn)} - ${formatDate(checkOut)}`;
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!owner) return { revenue: 0, cleaningFees: 0, adminFee: 0, subtotal: 0, costsTotal: 0, total: 0 };

    let revenue = 0;
    let cleaningFees = 0;

    owner.assignedProperties?.forEach(property => {
      const propRes = getPropertyReservations(property.name);
      propRes.forEach(res => {
        if (res.financials?.host?.revenue?.amount) {
          revenue += res.financials.host.revenue.amount / 100;
        }
      });
      cleaningFees += (property.cleaningFee || 0) * propRes.length;
    });

    const effectiveFees = owner.isCleaner ? 0 : cleaningFees;
    const adminFee = Math.max((revenue - effectiveFees) * 0.1, 0);
    const subtotal = revenue - effectiveFees - adminFee;
    const costsTotal = costs.reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);

    return { revenue, cleaningFees: effectiveFees, adminFee, subtotal, costsTotal, total: subtotal - costsTotal };
  };

  // Show decode/fetch errors
  if (decodeError || fetchError) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-red-400">{decodeError || fetchError}</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" />
      </div>
    );
  }

  const { start, end } = getWeekRange();
  const totals = calculateTotals();
  const weekIndicator = weekOffset === 0 ? 'Current Week' : weekOffset < 0 ? 'Past Week' : 'Future Week';

  const generateFilename = () => {
    const nameNoSpaces = ownerName.replace(/\s+/g, '');
    const month = String(end.getMonth() + 1).padStart(2, '0');
    const day = String(end.getDate()).padStart(2, '0');
    const year = end.getFullYear();
    return `${nameNoSpaces}${month}${day}${year}`;
  };

  const handlePrint = () => {
    const originalTitle = document.title;
    document.title = generateFilename();
    window.print();
    setTimeout(() => { document.title = originalTitle; }, 100);
  };

  return (
    <div className="print-outer min-h-screen bg-zinc-950 p-6">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-break { page-break-inside: avoid; }
          @page { margin: 1cm; }
          html, body { background: white !important; color: black !important; }
          .print-outer { background: white !important; padding: 0 !important; }
          .print-card { background: white !important; border: none !important; border-radius: 0 !important; box-shadow: none !important; max-width: 100% !important; padding: 0 !important; }
          .print-card * { color: #111 !important; border-color: #e5e7eb !important; background: white !important; }
          .print-card .print-header-name { color: #111 !important; font-size: 20pt !important; }
          .print-card .print-header-date { color: #666 !important; }
          .print-card .print-section-title { color: #111 !important; border-bottom: 1px solid #e5e7eb; padding-bottom: 4px; margin-bottom: 8px; }
          .print-card .print-prop-header { background: #f3f4f6 !important; }
          .print-card .print-prop-header * { color: #111 !important; }
          .print-card .print-th { color: #6b7280 !important; background: #f9fafb !important; }
          .print-card .print-th * { color: #6b7280 !important; }
          .print-card .print-total-pay { color: #111 !important; font-weight: bold; }
          .props-grid { display: grid !important; grid-template-columns: 1fr 1fr !important; gap: 1rem !important; }
        }
      `}</style>

      <div className="print-card max-w-4xl mx-auto bg-zinc-900 border border-zinc-800 rounded-lg p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 no-print">
          <div>
            <h1 className="text-2xl font-bold text-zinc-100">{ownerName}</h1>
            <p className="text-zinc-400 text-sm">{weekIndicator}: {start.toLocaleDateString()} - {end.toLocaleDateString()}</p>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded hover:bg-violet-700 text-sm"
          >
            <Printer className="w-4 h-4" />
            Print
          </button>
        </div>

        {/* Print Header */}
        <div className="hidden print:block mb-6">
          <h1 className="text-2xl font-bold text-zinc-900">{ownerName} - Weekly Report</h1>
          <p className="text-sm" style={{color: '#666'}}>{start.toLocaleDateString()} - {end.toLocaleDateString()}</p>
        </div>

        {/* Reservations by Property */}
        <div className="mb-6 print-break">
          <h2 className="text-base font-semibold text-zinc-200 mb-3">Reservations</h2>
          <div className={`props-grid ${owner?.assignedProperties?.filter(p => getPropertyReservations(p.name).length > 0).length > 1
            ? 'grid grid-cols-1 md:grid-cols-2 gap-4'
            : 'flex justify-center'}`}>
            {owner?.assignedProperties?.map(property => {
              const propRes = getPropertyReservations(property.name);
              if (propRes.length === 0) return null;

              return (
                <div key={property._id} className="border border-zinc-700 rounded overflow-hidden">
                  <div className="bg-zinc-800 px-4 py-2 border-b border-zinc-700">
                    <h3 className="font-medium text-zinc-200 text-sm">{property.name}</h3>
                  </div>
                  <table className="w-full">
                    <thead className="bg-zinc-800/50 text-xs uppercase text-zinc-500">
                      <tr>
                        <th className="px-4 py-2 text-left">Dates</th>
                        <th className="px-4 py-2 text-right">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {propRes.map(res => (
                        <tr key={res._id} className="border-b border-zinc-800 last:border-0">
                          <td className="px-4 py-2 text-sm text-zinc-300">
                            {formatDateRange(res.check_in, res.check_out)}
                          </td>
                          <td className="px-4 py-2 text-sm text-right font-medium text-zinc-200">
                            ${(res.financials?.host?.revenue?.amount / 100).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </div>

        {/* Costs Section */}
        {costs.length > 0 && (
          <div className="mb-6 print-break">
            <h2 className="text-base font-semibold text-zinc-200 mb-3">Additional Costs</h2>
            <table className="w-full">
              <tbody>
                {costs.map(cost => (
                  <tr key={cost.id} className="border-b border-zinc-800">
                    <td className="py-2 text-zinc-300 text-sm">
                      <span className="flex items-center gap-2">
                        {cost.name}
                        {cost.isRecurring && (
                          <span className="no-print text-xs bg-violet-900/40 text-violet-300 border border-violet-800 px-1.5 py-0.5 rounded flex items-center gap-1">
                            <RefreshCw className="w-3 h-3" />
                            Recurring
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="py-2 text-right font-medium text-zinc-200 text-sm">
                      ${parseFloat(cost.amount).toFixed(2)}
                    </td>
                    <td className="py-2 w-24 text-right no-print">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => toggleRecurring(cost.id)}
                          className={`p-1 rounded ${cost.isRecurring ? 'text-violet-400 hover:text-violet-300' : 'text-zinc-600 hover:text-zinc-400'}`}
                          title={cost.isRecurring ? 'Remove from recurring' : 'Make recurring'}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeCost(cost.id)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add Cost Form */}
        <div className="mb-6 no-print">
          <h2 className="text-base font-semibold text-zinc-200 mb-3">Add Cost</h2>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="text"
              placeholder="Description"
              value={newCost.name}
              onChange={(e) => setNewCost({ ...newCost, name: e.target.value })}
              className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-200 placeholder-zinc-500 text-sm focus:outline-none focus:border-violet-600"
            />
            <input
              type="number"
              placeholder="Amount"
              value={newCost.amount}
              onChange={(e) => setNewCost({ ...newCost, amount: e.target.value })}
              className="w-32 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-zinc-200 placeholder-zinc-500 text-sm focus:outline-none focus:border-violet-600"
              step="0.01"
            />
            <label className="flex items-center gap-1 text-sm text-zinc-400 whitespace-nowrap">
              <input
                type="checkbox"
                checked={newCost.isRecurring}
                onChange={(e) => setNewCost({ ...newCost, isRecurring: e.target.checked })}
                className="rounded"
              />
              Recurring
            </label>
            <button
              onClick={addCost}
              className="px-3 py-2 bg-violet-600 text-white rounded hover:bg-violet-700"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Totals */}
        <div className="border-t border-zinc-800 pt-6 print-break">
          <div className="max-w-xs ml-auto space-y-2">
            <div className="flex justify-between">
              <span className="text-zinc-400 text-sm">Total Revenue</span>
              <span className="font-medium text-zinc-200 text-sm">${totals.revenue.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400 text-sm">Cleaning Fees</span>
              <span className="font-medium text-zinc-200 text-sm">-${totals.cleaningFees.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-zinc-400 text-sm">Admin Fee (10%)</span>
              <span className="font-medium text-zinc-200 text-sm">-${totals.adminFee.toFixed(2)}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-zinc-800">
              <span className="text-zinc-400 text-sm">Subtotal</span>
              <span className="font-medium text-zinc-200 text-sm">${totals.subtotal.toFixed(2)}</span>
            </div>
            {totals.costsTotal > 0 && (
              <div className="flex justify-between">
                <span className="text-zinc-400 text-sm">Additional Costs</span>
                <span className="font-medium text-zinc-200 text-sm">-${totals.costsTotal.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-zinc-700 text-base font-bold">
              <span className="text-zinc-100">Total to Pay</span>
              <span className="text-emerald-400">${totals.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerReport;
